import { Component } from "@theme/component";

class ProductSubscriptionToggle extends Component {
  static get observedAttributes() {
    return ["data-variant-id"];
  }

  constructor() {
    super();
    this.blockId = null;
    this.productId = null;
    this.sectionId = null;
    this.currentVariantId = null;
    this.sellingPlans = [];
    this.selectedSellingPlanId = null;
    this.selectedOption = "one-time"; // Default to one-time
    this.radioInputs = [];
    this.optionLabels = [];
    this.addToCartComponents = [];
  }

  connectedCallback() {
    super.connectedCallback();
    this.init();
  }

  updatedCallback() {
    super.updatedCallback();
    // Store old product ID to detect product changes
    const oldProductId = this.productId;

    // Re-initialize when the component is updated by morph (e.g., from product-style-selector)
    // This ensures selling plans and other data are refreshed for the new product
    this.init();

    // If product ID changed (e.g., from style selector), reset subscription state
    const newProductId = this.dataset.productId ? parseInt(this.dataset.productId, 10) : null;
    if (oldProductId !== null && newProductId !== null && oldProductId !== newProductId) {
      // Product changed - reset subscription state to one-time
      this.selectedOption = "one-time";
      this.selectedSellingPlanId = null;
      this.updateSelectedState();
      // Clear subscription state from add-to-cart components
      this.updateAddToCartComponents();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "data-variant-id" && oldValue !== newValue) {
      this.currentVariantId = newValue ? parseInt(newValue, 10) : null;
      this.updatePrices();
    }
  }

  init() {
    this.blockId = this.dataset.blockId;
    this.productId = this.dataset.productId ? parseInt(this.dataset.productId, 10) : null;
    this.sectionId = this.dataset.sectionId;
    this.currentVariantId = this.dataset.variantId ? parseInt(this.dataset.variantId, 10) : null;

    // Parse selling plans data
    const sellingPlansScript = this.querySelector("script[data-selling-plans]");
    if (sellingPlansScript) {
      try {
        this.sellingPlans = JSON.parse(sellingPlansScript.textContent);
      } catch (e) {
        console.warn("[ProductSubscriptionToggle] Error parsing selling plans:", e);
      }
    }

    // Find all radio inputs
    this.radioInputs = Array.from(this.querySelectorAll('input[type="radio"][data-option-type]'));
    this.optionLabels = Array.from(this.querySelectorAll("[data-option]"));

    // Find linked Add to Cart components
    this.findAddToCartComponents();

    // Set up event listeners
    this.setupEventListeners();

    // Initialize state
    this.updateSelectedState();

    // Initialize Add to Cart components with default state (one-time)
    this.updateAddToCartComponents();

    // Clone shared benefits content to modal and tooltip
    this.cloneBenefitsContent();
  }

  cloneBenefitsContent() {
    // Find the shared benefits content container
    const sharedContainer = this.querySelector("[data-benefits-shared]");
    if (!sharedContainer) return;

    const sharedContent = sharedContainer.querySelector("div");
    if (!sharedContent) return;

    const contentHTML = sharedContent.innerHTML;

    // Clone content to modal (inside dialog)
    const modalContent = this.querySelector("dialog [data-benefits-content]");
    if (modalContent) {
      modalContent.innerHTML = contentHTML;
    }

    // Clone content to tooltip
    const tooltipContent = this.querySelector('[role="tooltip"] [data-benefits-content]');
    if (tooltipContent) {
      tooltipContent.innerHTML = contentHTML;
    }
  }

  findAddToCartComponents() {
    // Find Add to Cart components in the same section
    const sectionMatches = this.sectionId
      ? Array.from(document.querySelectorAll(`product-add-to-cart[data-section-id="${this.sectionId}"]`))
      : [];

    // Find Add to Cart components for this product
    const productMatches = this.productId
      ? Array.from(document.querySelectorAll(`product-add-to-cart[data-product-id="${this.productId}"]`))
      : [];

    // Merge unique components
    const allTargets = new Set([...sectionMatches, ...productMatches]);
    this.addToCartComponents = Array.from(allTargets);
  }

  setupEventListeners() {
    // Remove existing event listeners to avoid duplicates
    this.cleanupEventListeners();

    // Listen for radio button changes
    this.radioInputs.forEach(input => {
      input.addEventListener("change", this.handleOptionChange.bind(this));
    });

    // Listen for variant changes from option selectors
    this.variantChangeHandler = this.handleVariantChange.bind(this);
    document.addEventListener("variant:changed", this.variantChangeHandler);
  }

  cleanupEventListeners() {
    // Remove radio button event listeners
    this.radioInputs.forEach(input => {
      // Create a new handler function to match the one used in addEventListener
      // Note: We can't perfectly match bound functions, but removing all listeners
      // and re-adding them in setupEventListeners is safer
    });

    // Remove variant change handler
    if (this.variantChangeHandler) {
      document.removeEventListener("variant:changed", this.variantChangeHandler);
      this.variantChangeHandler = null;
    }
  }

  handleOptionChange(event) {
    const optionType = event.target.value;
    this.selectedOption = optionType;

    // Update visual state
    this.updateSelectedState();

    // Get the first selling plan if subscribing
    if (optionType === "subscribe" && this.sellingPlans.length > 0) {
      const firstGroup = this.sellingPlans[0];
      if (firstGroup.selling_plans && firstGroup.selling_plans.length > 0) {
        this.selectedSellingPlanId = firstGroup.selling_plans[0].id;
      }
    } else {
      this.selectedSellingPlanId = null;
    }

    // Update Add to Cart components
    this.updateAddToCartComponents();
  }

  handleVariantChange(event) {
    // Check if this event is for our product
    if (!event.detail) return;

    const eventProductId = event.detail.productId || event.detail.variant?.product_id;
    if (eventProductId && parseInt(eventProductId, 10) !== this.productId) {
      return; // Not for our product
    }

    // Update variant ID if it changed
    const variantId = event.detail.variantId || event.detail.variant?.id;
    if (variantId) {
      const newVariantId = parseInt(variantId, 10);
      if (newVariantId !== this.currentVariantId) {
        this.currentVariantId = newVariantId;
        this.dataset.variantId = newVariantId.toString();
        this.updatePrices();

        // Re-update Add to Cart components to ensure state is correct after variant change
        this.updateAddToCartComponents();
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanupEventListeners();
  }

  updateSelectedState() {
    // Update visual styling based on selection
    this.optionLabels.forEach(label => {
      const optionValue = label.dataset.option;
      const isSelected = optionValue === this.selectedOption;

      if (isSelected) {
        label.classList.add("product-subscription-toggle__option--selected");
        label.classList.remove("product-subscription-toggle__option--unselected");
      } else {
        label.classList.remove("product-subscription-toggle__option--selected");
        label.classList.add("product-subscription-toggle__option--unselected");
      }
    });
  }

  updatePrices() {
    // Prices are calculated server-side in Liquid
    // This method can be extended if dynamic price updates are needed
    // For now, prices are static from Liquid rendering
  }

  getSubscriptionPrice() {
    // Get subscription price from the DOM (calculated server-side in Liquid)
    const subscriptionPriceElement = this.querySelector(".product-subscription-toggle__price--subscription");
    if (!subscriptionPriceElement) {
      return null;
    }

    // Price format: "$159" with superscript "99"
    // Get the main price text (dollars)
    const mainText = subscriptionPriceElement.childNodes[0]?.textContent?.trim() || "";
    const dollarsMatch = mainText.match(/(\d+)/);
    if (!dollarsMatch) {
      return null;
    }
    const dollars = parseInt(dollarsMatch[1], 10);

    // Get cents from superscript
    const supElement = subscriptionPriceElement.querySelector("sup");
    let cents = 0;
    if (supElement) {
      const centsText = supElement.textContent.trim();
      const centsMatch = centsText.match(/(\d+)/);
      if (centsMatch) {
        cents = parseInt(centsMatch[1], 10);
      }
    }

    return dollars * 100 + cents;
  }

  getSubscriptionCompareAtPrice() {
    // Get compare at price (strikethrough price) from the DOM
    const comparePriceElement = this.querySelector(".product-subscription-toggle__price--compare");
    if (!comparePriceElement) {
      return null;
    }

    // Same format as subscription price
    const mainText = comparePriceElement.childNodes[0]?.textContent?.trim() || "";
    const dollarsMatch = mainText.match(/(\d+)/);
    if (!dollarsMatch) {
      return null;
    }
    const dollars = parseInt(dollarsMatch[1], 10);

    const supElement = comparePriceElement.querySelector("sup");
    let cents = 0;
    if (supElement) {
      const centsText = supElement.textContent.trim();
      const centsMatch = centsText.match(/(\d+)/);
      if (centsMatch) {
        cents = parseInt(centsMatch[1], 10);
      }
    }

    return dollars * 100 + cents;
  }

  updateAddToCartComponents() {
    if (this.addToCartComponents.length === 0) return;

    // Get subscription price if subscribing
    let subscriptionPrice = null;
    let subscriptionCompareAtPrice = null;

    if (this.selectedOption === "subscribe") {
      subscriptionPrice = this.getSubscriptionPrice();
      subscriptionCompareAtPrice = this.getSubscriptionCompareAtPrice();
    }

    // Store selling plan selection in a data attribute or custom property
    // The Add to Cart component will need to read this when adding to cart
    this.addToCartComponents.forEach(component => {
      if (this.selectedOption === "subscribe" && this.selectedSellingPlanId) {
        component.dataset.sellingPlanId = this.selectedSellingPlanId.toString();
        component.dataset.subscriptionMode = "subscribe";
        if (subscriptionPrice) {
          component.dataset.subscriptionPrice = subscriptionPrice.toString();
        }
        if (subscriptionCompareAtPrice) {
          component.dataset.subscriptionCompareAtPrice = subscriptionCompareAtPrice.toString();
        }
      } else {
        component.removeAttribute("data-selling-plan-id");
        component.removeAttribute("data-subscription-price");
        component.removeAttribute("data-subscription-compare-at-price");
        component.dataset.subscriptionMode = "one-time";
      }

      // Dispatch custom event to notify Add to Cart component
      // Event bubbles to document automatically (bubbles: true, composed: true)
      const eventDetail = {
        option: this.selectedOption,
        sellingPlanId: this.selectedSellingPlanId,
        subscriptionPrice: subscriptionPrice,
        subscriptionCompareAtPrice: subscriptionCompareAtPrice,
      };

      // Dispatch on component - will bubble to document automatically
      component.dispatchEvent(
        new CustomEvent("subscription:changed", {
          detail: eventDetail,
          bubbles: true,
          composed: true,
        })
      );
    });
  }

  // Public method to get current selection
  getSelection() {
    return {
      option: this.selectedOption,
      sellingPlanId: this.selectedSellingPlanId,
    };
  }
}

if (!customElements.get("product-subscription-toggle")) {
  customElements.define("product-subscription-toggle", ProductSubscriptionToggle);
}
